import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { io } from 'socket.io-client'
import { useAuth, api } from './AuthContext'
import { SOCKET_URL } from '../constants/api'

const SocketContext = createContext(null)

export const useSocket = () => {
  const ctx = useContext(SocketContext)
  if (!ctx) throw new Error('useSocket must be used within SocketProvider')
  return ctx
}

export function SocketProvider({ children }) {
  const { seller, token }  = useAuth()
  const socketRef          = useRef(null)
  const listenersRef       = useRef(new Map()) // event → Set<handler>

  const [connected,      setConnected]      = useState(false)
  const [notifications,  setNotifications]  = useState([])
  const [unreadCount,    setUnreadCount]    = useState(0)
  const [pendingCount,   setPendingCount]   = useState(0)
  const [chatUnread,     setChatUnread]     = useState(0)   // unread delivery chat messages
  const pendingDebounce = useRef(null)

  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await api.get('/orders/seller/incoming?status=pending&limit=1&page=1')
      setPendingCount(res.data.total ?? 0)
    } catch {}
  }, [])

  // Register a page-level listener for a socket event.
  // Returns an unsubscribe function — call it in a useEffect cleanup.
  const on = useCallback((event, handler) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set())
    }
    listenersRef.current.get(event).add(handler)
    return () => listenersRef.current.get(event)?.delete(handler)
  }, [])

  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data)
  }, [])

  const clearUnread     = useCallback(() => setUnreadCount(0), [])
  const clearChatUnread = useCallback(() => setChatUnread(0),  [])

  // Fetch pending count once when logged in
  useEffect(() => {
    if (token && seller) fetchPendingCount()
  }, [token, seller?.userId, fetchPendingCount])

  useEffect(() => {
    if (!token || !seller) return

    const socket = io(SOCKET_URL, {
      path:                '/api/socket.io',
      auth:                { token },
      transports:          ['websocket', 'polling'],
      reconnectionAttempts: 8,
      reconnectionDelay:   2000,
    })

    socketRef.current = socket

    socket.on('connect', () => {
      setConnected(true)
      socket.emit('user:online', { role: seller.role, shopId: seller.shopId })
    })

    socket.on('connect_error', (err) => {
      console.warn('[Socket] connect error:', err.message)
    })

    socket.on('disconnect', () => setConnected(false))

    const debouncePending = () => {
      clearTimeout(pendingDebounce.current)
      pendingDebounce.current = setTimeout(fetchPendingCount, 800)
    }

    // ── Order notifications ──────────────────────────────────────────────────
    socket.on('notification', (notif) => {
      setNotifications(prev => [{ ...notif, _id: `${Date.now()}_${Math.random()}` }, ...prev].slice(0, 50))
      setUnreadCount(c => c + 1)
      if (notif.type === 'new_order') {
        setPendingCount(c => c + 1)
        debouncePending()
      }
      listenersRef.current.get('notification')?.forEach(h => h(notif))
    })

    // ── Order status changed ─────────────────────────────────────────────────
    socket.on('order:update', (payload) => {
      if (payload?.status && payload.status !== 'pending') {
        setPendingCount(c => Math.max(0, c - 1))
        debouncePending()
      }
      listenersRef.current.get('order:update')?.forEach(h => h(payload))
    })

    // ── Live stock updates ───────────────────────────────────────────────────
    socket.on('live:product_qty_update', (payload) => {
      listenersRef.current.get('live:product_qty_update')?.forEach(h => h(payload))
    })

    // ── Quick-live new order ─────────────────────────────────────────────────
    socket.on('quicklive:new_order', (payload) => {
      setPendingCount(c => c + 1)
      debouncePending()
      listenersRef.current.get('quicklive:new_order')?.forEach(h => h(payload))
    })

    // ── Delivery chat ────────────────────────────────────────────────────────
    // These were missing — without them on() handlers in DeliveryChat never fire

    socket.on('delivery:joined', (payload) => {
      listenersRef.current.get('delivery:joined')?.forEach(h => h(payload))
    })

    socket.on('delivery:history', (payload) => {
      listenersRef.current.get('delivery:history')?.forEach(h => h(payload))
    })

    socket.on('delivery:message', (payload) => {
      // Track global unread count for sidebar badge + toast
      setChatUnread(c => c + 1)
      listenersRef.current.get('delivery:message')?.forEach(h => h(payload))
    })

    return () => {
      clearTimeout(pendingDebounce.current)
      socket.disconnect()
      socketRef.current = null
      setConnected(false)
    }
  }, [token, seller?.userId, seller?.shopId, fetchPendingCount])

  return (
    <SocketContext.Provider value={{
      connected, notifications,
      unreadCount, clearUnread,
      pendingCount, setPendingCount,
      chatUnread,  clearChatUnread,
      emit, on,
    }}>
      {children}
    </SocketContext.Provider>
  )
}
